import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, BookOpen, Check, X, UserCheck, UserX, Save } from 'lucide-react';
import api from '../../services/api';

export default function LecturerAttendanceDetail() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [students, setStudents] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => { loadData(); }, [sessionId]);

  const loadData = async () => {
    try {
      const res = await api.get(`/lecturer/attendance/${sessionId}`);
      if (res.data.success) {
        setSession(res.data.data.session);
        setStudents(res.data.data.students || []);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load attendance details');
    } finally {
      setLoading(false);
    }
  };

  const toggleAttendance = (studentId) => {
    setStudents(prev => prev.map(s => {
      if (s.user_id === studentId) {
        const currentStatus = s.status || 'Absent'; // Default unmarked to Absent
        let newStatus = 'Present';
        if (currentStatus === 'Present') newStatus = 'Absent';
        return { ...s, status: newStatus };
      }
      return s;
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setError('');
      const attendanceRecords = students.map(s => ({
        studentId: s.user_id,
        status: s.status || 'Absent'
      }));
      const res = await api.post(`/lecturer/attendance/${sessionId}`, {
        records: attendanceRecords
      });
      
      if (res.data.success) {
        setSuccess('Attendance updated successfully!');
        setHasChanges(false);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update attendance');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  if (error && !session) {
    return (
      <div className="space-y-6">
        <button onClick={() => navigate('/lecturer/attendance')} className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div>
      </div>
    );
  }

  if (!session) return <div>No session found</div>;

  const presentCount = students.filter(s => s.status === 'Present').length;
  const absentCount = students.filter(s => s.status === 'Absent' || !s.status).length;
  const presentPercentage = students.length > 0 ? ((presentCount / students.length) * 100).toFixed(1) : 0;

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button onClick={() => navigate('/lecturer/attendance')} className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium">
        <ArrowLeft className="w-4 h-4" /> Back to Sessions
      </button>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-green-700">{success}</div>
      )}

      {/* Session Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">{session.subject_name}</h1>
            <p className="text-gray-500 mt-2">{session.grade_name}</p>
          </div>
          <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full font-medium">
            {new Date(session.session_date).toLocaleDateString()}
          </span>
        </div>

        {session.session_description && (
          <p className="mt-4 text-gray-600"><strong>Description:</strong> {session.session_description}</p>
        )}
      </div>

      {/* Attendance Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Total Students</p>
          <p className="text-3xl font-bold text-gray-800 mt-2">{students.length}</p>
        </div>
        <div className="bg-green-50 rounded-xl shadow-sm border border-green-100 p-4">
          <p className="text-sm text-green-600">Present</p>
          <div className="mt-2">
            <p className="text-3xl font-bold text-green-600">{presentCount}</p>
            <p className="text-xs text-green-500 mt-1">{presentPercentage}%</p>
          </div>
        </div>
        <div className="bg-red-50 rounded-xl shadow-sm border border-red-100 p-4">
          <p className="text-sm text-red-600">Absent (includes unmarked)</p>
          <p className="text-3xl font-bold text-red-600 mt-2">{absentCount}</p>
        </div>
      </div>

      {/* Attendance Records */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Student Attendance</h2>
            <p className="text-gray-500 text-sm mt-1">{students.length} students in this session</p>
          </div>
          {hasChanges && (
            <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition">
              <Save className="w-4 h-4" /> Save Changes
            </button>
          )}
        </div>

        <div className="divide-y divide-gray-100">
          {students.map((student) => {
            const status = student.status || 'Absent'; // Treat unmarked as Absent
            const isPresent = status === 'Present';
            const isAbsent = status === 'Absent';
            const isUnmarked = !student.status;

            return (
              <div
                key={student.user_id}
                onClick={() => toggleAttendance(student.user_id)}
                className={`flex items-center justify-between p-4 cursor-pointer transition ${
                  isPresent
                    ? 'bg-green-50 hover:bg-green-100'
                    : isUnmarked
                    ? 'bg-gray-50 hover:bg-gray-100'
                    : 'bg-red-50 hover:bg-red-100'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white ${
                      isPresent
                        ? 'bg-green-500'
                        : isUnmarked
                        ? 'bg-gray-400'
                        : 'bg-red-500'
                    }`}
                  >
                    {student.full_name?.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">{student.full_name}</p>
                    <p className="text-sm text-gray-500">{student.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 ${
                      isPresent
                        ? 'bg-green-100 text-green-700'
                        : isUnmarked
                        ? 'bg-gray-200 text-gray-600'
                        : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {isPresent && <Check className="w-4 h-4" />}
                    {isAbsent && <X className="w-4 h-4" />}
                    {isPresent ? 'Present' : isUnmarked ? 'Absent (default)' : 'Absent'}
                  </span>
                </div>
              </div>
            );
          })}
          {students.length === 0 && (
            <div className="px-6 py-8 text-center text-gray-400">
              No students in this session
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      {hasChanges && (
        <div className="flex gap-3">
          <button
            onClick={() => loadData()}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
          >
            Discard Changes
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium flex items-center justify-center gap-2"
          >
            <Save className="w-4 h-4" /> Save All Changes
          </button>
        </div>
      )}
    </div>
  );
}
